import {Typography} from '@mui/material'
import {PageLayout} from '@goschool/components'
import {useParams} from 'react-router-dom'

import type {QueryDocumentSnapshot} from 'firebase/firestore'
import type {Course, CourseChat} from '@goschool/model'
import {useChat, useCourse} from '@goschool/model'
import {useUserContext} from '@goschool/auth'

import {ChatContextProvider, ChatMessages} from '@goschool/shared/chat'
import {NotFound} from '@goschool/react-firebase'
import {GoSchool} from '@goschool/routing'
import {useChatManager} from './components/useChatManager'
import {useMemo} from 'react'
import {useCourseChatsBreadcrumbs} from './CourseChatsPage'
import {CourseChatLayout} from './CourseChatPage'


export function CourseChatViewPage() {
  const {organizationId, courseId, chatId} = useParams<{
    organizationId: string
    courseId: string
    chatId: string
  }>()

  const courseSnapshot = useCourse(organizationId, courseId)
  const courseChatSnapshot = useChat(organizationId, courseId, chatId)
  const {user} = useUserContext()

  const isParticipant = useMemo(() => {
    if (user == null || courseChatSnapshot == null || !courseChatSnapshot.exists()) {
      return false
    }
    const isOwner = courseChatSnapshot.data().created_by === user.uid
    const participants = courseChatSnapshot.data().participants

    return isOwner || participants.includes(user.uid)
  }, [user, courseChatSnapshot])

  if (courseSnapshot === undefined || courseChatSnapshot === undefined) {
    return null
  }

  if (!courseSnapshot?.exists() || !courseChatSnapshot?.exists()) {
    throw new NotFound()
  }

  if (isParticipant) {
    return <CourseChatLayout courseSnapshot={courseSnapshot} courseChatSnapshot={courseChatSnapshot}/>
  }

  return <CourseChatViewLayout courseSnapshot={courseSnapshot} courseChatSnapshot={courseChatSnapshot}/>
}

function useCourseChatViewBreadcrumbs(
  courseSnapshot: QueryDocumentSnapshot<Course>,
  selectedChat: QueryDocumentSnapshot<CourseChat>) {
  const chatBreadcrumbs = useCourseChatsBreadcrumbs(courseSnapshot)
  return [
    ...chatBreadcrumbs,
    {
      title: selectedChat.data().title ?? 'New Chat',
      href: GoSchool.courseChatView(selectedChat.ref)
    }
  ]
}

function CourseChatViewLayout({courseSnapshot, courseChatSnapshot}: {
  courseSnapshot: QueryDocumentSnapshot<Course>,
  courseChatSnapshot: QueryDocumentSnapshot<CourseChat>
}) {
  const {user} = useUserContext()
  const chatRef = useMemo(() => courseChatSnapshot.ref, [courseChatSnapshot])

  const chatManager = useChatManager(user, chatRef)

  return <ChatContextProvider chatManager={chatManager} mode="view">
    <PageLayout
      fullScreen={false}
      breadcrumbs={useCourseChatViewBreadcrumbs(courseSnapshot, courseChatSnapshot)}>
      <Typography variant="h4" component="h1"
        gutterBottom={true}>{courseChatSnapshot.data().title ?? 'New Chat'}</Typography>
      <ChatMessages/>
    </PageLayout>
  </ChatContextProvider>
}
